import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Package } from 'lucide-react'
import { useMenuManagementStore } from '../../core/stores/menuManagementStore'
import { useTranslation } from '../../core/hooks/useTranslation'
import { MenuCategory } from '../../core/models/menu'
import Modal from '../../components/ui/Modal'
import Input from '../../components/ui/Input'
import Textarea from '../../components/ui/Textarea'

type CreateItemFormData = {
  categoryId: number
  name: string
  description?: string
  price: number
  sortOrder?: number
  isAvailable?: boolean
}

interface CreateItemModalProps {
  businessId: number
  categories: MenuCategory[]
  isOpen: boolean
  onClose: () => void
  onItemCreated?: () => void
  defaultCategoryId?: number | null
}

export default function CreateItemModal({ categories, isOpen, onClose, onItemCreated, defaultCategoryId }: CreateItemModalProps) {
  const { t } = useTranslation()
  const { createItem, isLoading, items } = useMenuManagementStore()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createItemSchema = z.object({
    categoryId: z.number().min(1, t('itemValidationCategoryRequired')),
    name: z.string().min(1, t('itemValidationNameRequired')).max(100, t('itemValidationNameMax')),
    description: z.string().max(500, t('itemValidationDescriptionMax')).optional(),
    price: z.preprocess(
      (val) => {
        if (val === '' || val === null || val === undefined || isNaN(Number(val))) {
          return undefined
        }
        return Number(val)
      },
      z.number({ required_error: t('itemValidationPriceRequired'), invalid_type_error: t('itemValidationPriceInvalid') })
        .min(0.01, t('itemValidationPriceMinValue'))
    ),
    sortOrder: z.preprocess(
      (val) => {
        if (val === '' || val === null || val === undefined || isNaN(Number(val))) {
          return undefined
        }
        return Number(val)
      },
      z.number().min(0, t('itemValidationSortOrderPositive')).optional()
    ),
    isAvailable: z.boolean().optional()
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<CreateItemFormData>({
    resolver: zodResolver(createItemSchema),
    defaultValues: {
      categoryId: defaultCategoryId || undefined,
      sortOrder: items.length + 1,
      isAvailable: true
    }
  })

  const selectedCategoryId = watch('categoryId')

  // Set default category when modal opens or when defaultCategoryId changes
  useEffect(() => {
    if (isOpen && defaultCategoryId) {
      setValue('categoryId', defaultCategoryId, { shouldValidate: false })
    }
  }, [isOpen, defaultCategoryId, setValue])

  const onSubmit = async (data: CreateItemFormData) => {
    try {
      setIsSubmitting(true)
      await createItem({
        categoryId: data.categoryId,
        name: data.name,
        description: data.description || undefined,
        price: data.price,
        sortOrder: data.sortOrder || items.length + 1,
        isAvailable: data.isAvailable ?? true
      })
      reset({
        categoryId: defaultCategoryId || undefined,
        sortOrder: items.length + 1,
        isAvailable: true
      })
      onClose()
      // Refresh items after create
      if (onItemCreated) {
        onItemCreated()
      }
    } catch (error) {
      // Error is handled in the store
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset({
      categoryId: defaultCategoryId || undefined,
      sortOrder: items.length + 1,
      isAvailable: true
    })
    onClose()
  }

  if (!isOpen) return null

  const activeCategories = categories.filter(c => c.isVisible)

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-blue-600" />
          <span>{t('addNewItem')}</span>
        </div>
      }
      actionButtonText={t('save')}
      onAction={handleSubmit(onSubmit)}
      actionButtonLoading={isSubmitting || isLoading}
      actionButtonDisabled={isSubmitting || isLoading || !selectedCategoryId}
      actionButtonLoadingText={t('saving')}
      actionButtonClassName="text-sm h-10 py-2 px-4"
      showCancelButton={true}
      cancelButtonText={t('cancel')}
      cancelButtonClassName="border-gray-300 text-[#1E293B] hover:text-[#1E293B] hover:border-[#1E293B] hover:bg-gray-50 text-sm h-10 py-2 px-4"
    >
      <form className="space-y-4">
        <div>
          <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">
            {t('category')} *
          </label>
          <select
            id="categoryId"
            {...register('categoryId', { valueAsNumber: true })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400"
          >
            <option value="">{t('categorySelectPlaceholder')}</option>
            {activeCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {errors.categoryId && (
            <p className="mt-1 text-sm text-red-600">{errors.categoryId.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {t('itemNameLabel')} *
          </label>
          <Input
            id="name"
            {...register('name')}
            placeholder={t('itemNamePlaceholder')}
            error={errors.name?.message}
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            {t('itemDescriptionLabel')} ({t('itemDescriptionOptional')})
          </label>
          <Textarea
            id="description"
            {...register('description')}
            rows={3}
            placeholder={t('itemDescriptionPlaceholder')}
            error={errors.description?.message}
          />
        </div>

        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
            {t('itemPriceLabel')} ({t('currency')}) *
          </label>
          <Input
            id="price"
            type="number"
            step="0.01"
            min="0.01"
            {...register('price', { valueAsNumber: true })}
            placeholder="0.00"
            error={errors.price?.message}
          />
        </div>

        <div>
          <label htmlFor="sortOrder" className="block text-sm font-medium text-gray-700 mb-1">
            {t('itemSortOrderLabel')}
          </label>
          <Input
            id="sortOrder"
            type="number"
            min="1"
            {...register('sortOrder', { valueAsNumber: true })}
            placeholder="1"
            error={errors.sortOrder?.message}
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('itemSortOrderHelper')}
          </p>
        </div>

        <div className="flex items-center">
          <input
            id="isAvailable"
            type="checkbox"
            {...register('isAvailable')}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            defaultChecked
          />
          <label htmlFor="isAvailable" className="mr-2 block text-sm text-gray-900">
            {t('itemAvailableLabel')}
          </label>
        </div>
      </form>
    </Modal>
  )
}